/**
 * Legal content pages (Aviso de privacidad, Términos y condiciones, Política
 * de venta, Política de devoluciones — one shared script): the mobile TOC disclosure,
 * active-section tracking for the table of contents (block + section), and
 * accessible anchor navigation (manual scroll + focus move) for every jump
 * link on the page — TOC links, the mobile panel's links and the per-block
 * "Volver al índice" links. No dependencies; IntersectionObserver is the
 * only browser API doing real work here.
 */
(function initLegalContentPage() {
  const page = document.querySelector('.legal-page');
  if (!page) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Mobile disclosure: a plain button + panel, not <details> — plain JS is
   * needed anyway for "close the panel, then jump" when a link inside is
   * activated, so <details> would add nothing the explicit
   * aria-expanded/aria-controls pair doesn't already cover.
   */
  const mobileTrigger = page.querySelector('[data-legal-toc-mobile-trigger]');
  const mobilePanel = mobileTrigger ? document.getElementById(mobileTrigger.getAttribute('aria-controls')) : null;

  function setMobileTocOpen(open) {
    if (!mobileTrigger || !mobilePanel) return;
    mobileTrigger.setAttribute('aria-expanded', String(open));
    mobilePanel.hidden = !open;
  }

  mobileTrigger?.addEventListener('click', () => {
    setMobileTocOpen(mobileTrigger.getAttribute('aria-expanded') !== 'true');
  });

  /**
   * Every same-page jump on this page goes through here: close the mobile
   * panel first if it's open (so the next thing the user sees is the
   * destination, not a stray open disclosure), scroll manually (respecting
   * reduced motion), then move focus to the target so keyboard and
   * screen-reader users land where sighted users land.
   */
  // The section the reader just jumped to. In a short document the last
  // sections can never be scrolled up to the reading line (the page ends
  // first), so the scroll position alone would highlight the wrong entry;
  // until the reader scrolls by hand, the clicked section stays current.
  let pinnedTarget = null;

  page.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const targetId = link.getAttribute('href').slice(1);
    const target = targetId ? document.getElementById(targetId) : null;
    if (!target) return;

    event.preventDefault();
    setMobileTocOpen(false);
    pinnedTarget = target;
    if (page.classList.contains('is-enhanced')) update();

    target.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });

    // preventDefault() above also suppresses the browser's own hash update,
    // which made section links impossible to copy or share. replaceState (not
    // pushState) keeps one history entry, so "Back" still leaves the page
    // instead of stepping through every section the reader visited. It never
    // fires a hashchange/scroll, so it cannot fight the manual scroll above.
    try {
      history.replaceState(null, '', link.getAttribute('href'));
    } catch (error) {
      // Sandboxed contexts can refuse replaceState; navigation still works.
    }

    // Focus lands on the target's own heading (or the disclosure button
    // for the "Volver al índice" jump) — never on the whole block, which
    // would draw a focus ring around pages of text. Headings aren't
    // naturally focusable, so a temporary tabindex lets focus land there
    // without adding every heading to the page's normal tab order.
    const focusTarget = target.matches('h1, h2, h3, h4')
      ? target
      : target.querySelector('h2, h3, h4') || target.querySelector('button') || target;
    const isNaturallyFocusable = focusTarget.matches('a, button, input, select, textarea, [tabindex]');
    if (!isNaturallyFocusable) {
      focusTarget.setAttribute('tabindex', '-1');
      focusTarget.addEventListener('blur', () => focusTarget.removeAttribute('tabindex'), { once: true });
    }
    focusTarget.focus({ preventScroll: true });
  });

  /**
   * Active TOC state. "Current" = the last TOC target whose top has
   * passed the reading line just under the sticky header (the same line a
   * clicked anchor lands on, thanks to scroll-margin-top). The observer's
   * root is a 1px band exactly at that line, so a callback fires the
   * moment any target's edge crosses it — no scroll listener, no duplicated
   * magic numbers (the header height comes from --header-height).
   *
   * Two levels: the current SECTION's link gets the active mark, and so
   * does its parent BLOCK's link (the sidebar expands only the active
   * block's sub-list; see .is-enhanced in the section styles).
   */
  const tocLinks = Array.from(page.querySelectorAll('[data-legal-toc-link]'));
  const targets = [];
  tocLinks.forEach((link) => {
    const el = document.getElementById(link.dataset.legalTocLink);
    if (el && !targets.includes(el)) targets.push(el);
  });
  // TOC order is document order, but sort defensively.
  targets.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

  if (targets.length === 0 || !('IntersectionObserver' in window)) return;
  page.classList.add('is-enhanced');

  /**
   * --header-height is declared as a calc() expression (e.g. "calc(4.25rem + 1px)"),
   * which parseFloat() cannot read — it silently fell back to a fixed 68px.
   * A hidden probe whose CSS height IS that variable lets the browser resolve
   * the real pixel value (rem, calc and all) with no duplicated magic number.
   */
  const headerProbe = document.createElement('div');
  headerProbe.setAttribute('aria-hidden', 'true');
  headerProbe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;width:0;height:var(--header-height, 4.25rem);';
  page.appendChild(headerProbe);

  function readingLine() {
    const rootStyle = getComputedStyle(document.documentElement);
    const headerHeight = headerProbe.offsetHeight || 68;
    const rem = parseFloat(rootStyle.fontSize) || 16;
    return headerHeight + rem * 2;
  }

  function setActive(currentEl) {
    const parentGroup = currentEl.classList.contains('legal-page__group') ? currentEl : currentEl.closest('.legal-page__group');
    tocLinks.forEach((link) => {
      const id = link.dataset.legalTocLink;
      const isActive = id === currentEl.id || (parentGroup && id === parentGroup.id);
      link.toggleAttribute('data-legal-toc-active', Boolean(isActive));
    });
    page.querySelectorAll('.legal-page__toc-group').forEach((li) => {
      const groupLink = li.querySelector(':scope > a[data-legal-toc-link]');
      li.toggleAttribute('data-legal-toc-group-active', Boolean(parentGroup && groupLink?.dataset.legalTocLink === parentGroup.id));
    });
  }

  function atPageBottom() {
    return window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
  }

  function update() {
    const line = readingLine();
    let current = targets[0];
    for (const el of targets) {
      if (el.getBoundingClientRect().top <= line + 1) current = el;
      else break;
    }
    // Page end reached: the last section is the one being read, even when
    // it is too short to ever reach the reading line.
    if (pinnedTarget && targets.includes(pinnedTarget)) current = pinnedTarget;
    else if (atPageBottom()) current = targets[targets.length - 1];
    setActive(current);
  }

  // Reader scrolls by hand: the pinned section no longer applies.
  const SCROLL_KEYS = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];
  ['wheel', 'touchmove', 'keydown'].forEach((type) => {
    window.addEventListener(type, (event) => {
      if (type === 'keydown' && !SCROLL_KEYS.includes(event.key)) return;
      if (pinnedTarget) {
        pinnedTarget = null;
        update();
      }
    }, { passive: true });
  });

  // The observer only fires when a heading crosses the reading line; reaching
  // the end of the page without crossing one needs this cheap check.
  let bottomTicking = false;
  window.addEventListener('scroll', () => {
    if (bottomTicking || !atPageBottom()) return;
    bottomTicking = true;
    requestAnimationFrame(() => {
      bottomTicking = false;
      update();
    });
  }, { passive: true });

  let observer;
  function observe() {
    observer?.disconnect();
    const line = readingLine();
    const bottomMargin = Math.max(window.innerHeight - line - 1, 0);
    observer = new IntersectionObserver(update, {
      rootMargin: `-${line}px 0px -${bottomMargin}px 0px`,
      threshold: [0, 1],
    });
    targets.forEach((el) => observer.observe(el));
    update();
  }

  observe();

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(observe, 150);
  });
})();
